/**
 * @quotech/auth - Protected Route Component
 * 
 * PATH: packages/auth/src/ProtectedRoute.tsx
 * PURPOSE: Wraps routes that require authentication
 * 
 * FEATURES:
 * - Checks cookie auth first (for cross-hub navigation)
 * - Waits for SSO token processing if present in URL
 * - Redirects to MASTER LOGIN on session expiration
 * - Preserves returnTo URL for post-login redirect
 * - Listens for cross-tab logout events
 * 
 * REDIRECT FLOW:
 * - Session expired → Master Login with reason=session_expired
 * - Not authenticated → Master Login with reason=invalid
 * - Cross-tab logout → Master Login with reason=logged_out
 */

import { useEffect, useState, useRef, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from './authStore'
import { MASTER_LOGIN_URL, type LogoutReason } from './cookieAuth'

export interface ProtectedRouteProps {
  /** Content to render when authenticated */
  children: ReactNode
  /** 
   * URL to redirect to when not authenticated
   * @default MASTER_LOGIN_URL (https://dashboard.quotech.ai/login)
   */
  redirectTo?: string
  /** Custom loading component */
  loadingComponent?: ReactNode
  /** Callback when auth check fails */
  onAuthFailed?: (reason: LogoutReason) => void
  /** 
   * Whether to use master login redirect (with returnTo and reason)
   * @default true
   */
  useMasterLogin?: boolean
}

/**
 * Default loading component - matches Quotech ERP design system
 */
function DefaultLoading() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading...</p>
      </div>
    </div>
  )
}

/**
 * Check if there's an SSO token in the URL that needs processing
 */
function hasSSOToken(searchParams: URLSearchParams): boolean {
  return searchParams.has('token')
}

/**
 * Build master login URL with returnTo and reason
 */
function buildMasterLoginUrl(reason: LogoutReason, returnTo?: string): string {
  const currentUrl = returnTo || (typeof window !== 'undefined' ? window.location.href : '')
  const params = new URLSearchParams()
  
  if (currentUrl && !currentUrl.includes('/login')) {
    params.set('returnTo', currentUrl)
  }
  params.set('reason', reason)
  
  return `${MASTER_LOGIN_URL}?${params.toString()}`
}

// Cross-tab logout event key
const LOGOUT_EVENT_KEY = 'quotech:logout'

export function ProtectedRoute({
  children,
  redirectTo,
  loadingComponent,
  onAuthFailed,
  useMasterLogin = true,
}: ProtectedRouteProps) {
  const [searchParams] = useSearchParams()
  const { isAuthenticated, checkAuth, clearAuth } = useAuthStore()
  const [waitingForSSO, setWaitingForSSO] = useState(() => hasSSOToken(searchParams))
  
  // Track our own auth check state to avoid race conditions with Zustand rehydration
  const [authCheckComplete, setAuthCheckComplete] = useState(false)
  const [authCheckResult, setAuthCheckResult] = useState<boolean | null>(null)
  const checkStartedRef = useRef(false)
  const redirectingRef = useRef(false)

  // Determine redirect URL
  const getRedirectUrl = (reason: LogoutReason): string => {
    if (redirectTo && !useMasterLogin) {
      return redirectTo
    }
    return buildMasterLoginUrl(reason)
  }

  // Handle redirect to login
  const handleRedirect = (reason: LogoutReason) => {
    if (redirectingRef.current) return
    redirectingRef.current = true
    
    console.log(`[@quotech/auth] Session invalid (${reason}), redirecting to master login`)
    onAuthFailed?.(reason)
    
    const url = getRedirectUrl(reason)
    window.location.replace(url)
  }

  // Auth check on mount
  useEffect(() => {
    // Prevent double-checking (React 18 strict mode)
    if (checkStartedRef.current) return
    checkStartedRef.current = true

    const performAuthCheck = async () => {
      try {
        // Wait for checkAuth to complete - this properly checks cookies and tokens
        const isValid = await checkAuth()
        setAuthCheckResult(isValid)
      } catch (error) {
        console.error('[@quotech/auth] Auth check failed:', error)
        setAuthCheckResult(false)
      } finally {
        setAuthCheckComplete(true)
      }
    }

    performAuthCheck()
  }, [checkAuth])

  // Listen for cross-tab logout events
  useEffect(() => {
    if (typeof window === 'undefined') return

    // localStorage event (from other tabs)
    const handleStorageLogout = (e: StorageEvent) => {
      if (e.key === LOGOUT_EVENT_KEY && e.newValue) {
        console.log('[@quotech/auth] Cross-tab logout detected')
        clearAuth()
        handleRedirect('logged_out')
      }
    }

    // Custom event (from same tab)
    const handleCustomLogout = () => {
      console.log('[@quotech/auth] Same-tab logout detected')
      handleRedirect('logged_out')
    }

    window.addEventListener('storage', handleStorageLogout)
    window.addEventListener('quotech:logout', handleCustomLogout)

    return () => {
      window.removeEventListener('storage', handleStorageLogout)
      window.removeEventListener('quotech:logout', handleCustomLogout)
    }
  }, [clearAuth])

  // Watch for SSO token to be processed (removed from URL)
  useEffect(() => {
    if (waitingForSSO && !hasSSOToken(searchParams)) {
      // Token was processed, stop waiting
      setWaitingForSSO(false)
    }
  }, [searchParams, waitingForSSO])

  // Watch for successful authentication while waiting for SSO
  useEffect(() => {
    if (waitingForSSO && isAuthenticated) {
      // SSO succeeded, stop waiting
      setWaitingForSSO(false)
    }
  }, [isAuthenticated, waitingForSSO])

  // Handle redirect when auth fails
  useEffect(() => {
    // Don't redirect while waiting for SSO to process
    if (waitingForSSO) return
    
    // Only redirect after auth check is complete AND result is negative
    if (authCheckComplete && authCheckResult === false && !isAuthenticated) {
      handleRedirect('session_expired')
    }
  }, [authCheckComplete, authCheckResult, isAuthenticated, waitingForSSO])

  // Show loading while SSO is processing
  if (waitingForSSO) {
    return <>{loadingComponent || <DefaultLoading />}</>
  }

  // Show loading while checking auth
  if (!authCheckComplete) {
    return <>{loadingComponent || <DefaultLoading />}</>
  }

  // Auth check failed and store confirms not authenticated - will redirect
  if (authCheckResult === false && !isAuthenticated) {
    return <>{loadingComponent || <DefaultLoading />}</>
  }

  // Authenticated - render children
  return <>{children}</>
}

export default ProtectedRoute
